<template>
  <step-template :subStep="subStep">
    <v-card-text>
      <!-- Banner Image -->
      <v-row v-if="bannerSrc">
        <v-col>
          <img
            :lazy-src="bannerSrc"
            contain
            :src="bannerSrc"
            style="margin: 0 auto;"
          />
        </v-col>
      </v-row>

      <!-- Title -->
      <v-row v-if="title" style="width: 100%;">
        <v-col class="mx-8 mt-5 mb-0">
          <h3 class="text-center text-break" v-html="title"></h3>
        </v-col>
      </v-row>

      <!-- Message Above -->
      <v-row v-if="messageAbove" style="width: 100%;">
        <v-col>
          <h5 class="text-center text-break" v-html="messageAbove"></h5>
        </v-col>
      </v-row>

      <!-- Message Box -->
      <v-row v-if="messageBox" style="width: 100%;">
        <v-col>
          <v-sheet
            class="pa-6 mx-8 mt-5 mb-0"
            elevation="3"
            rounded
            :color="messageBoxBgColor"
          >
            <h5
              class="text-center text-break"
              v-html="messageBox"
              :style="`color:${messageBoxTxtColor}`"
            ></h5>
          </v-sheet>
        </v-col>
      </v-row>

      <!-- Dynamic HTML Content -->
      <v-row style="width: 100%;">
        <v-col>
          <div class="html-content mx-8 mt-5 mb-0" v-html="htmlContent"></div>
        </v-col>
      </v-row>

      <!-- Message Below -->
      <v-row v-if="messageBelow" style="width: 100%;">
        <v-col>
          <h5 class="text-center text-break mx-8 mt-5 mb-0" v-html="messageBelow"></h5>
        </v-col>
      </v-row>

      <!-- Action Buttons -->
      <v-row v-if="actionButtons" class="mt-5 justify-center">
        <v-col class="d-flex flex-wrap justify-center">
          <v-btn
            v-for="(button, key) in actionButtons"
            :key="key"
            :class="`${button.class || ''} mr-2 mb-2`"
            large
            elevation="2"
            rounded
            :color="button.color || 'primary'"
            @click="customButtonClick(button)"
          >
            <span>{{ button.label }}</span>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Continue Message -->
      <v-row style="width: 100%;">
        <v-col>
          <h5 class="text-center text-break mx-8 mt-5 mb-0">
            Click <span style="color:#ff9800;"><b>CONTINUE</b></span> to proceed.
          </h5>
        </v-col>
      </v-row>
    </v-card-text>
  </step-template>
</template>

<script>
/**
 * To use:
 - in the database, go to table substeps
 - find the substep row that you want this component
 - update the row field component='step-html'
 - update the row field component_props as a JSON like:

* SIMPLE HTML
{ "html": "<p>This is a simple <strong>HTML snippet</strong> to display.</p>" }

OR

* Add Messages Boxed = messageBox / Unboxed = messageAbove/messageBelow
{
  "html": "<p>Main content here</p>",
  "bannerSrc": "/img/my-banner.jpg",
  "title": "Page Title",
  "messageAbove": "This message shows above the HTML content.",
  "messageBox": "This message shows inside a box with custom color options.",
  "messageBoxTxtColor": "white",
  "messageBoxBgColor": "green",
  "messageBelow": "This message shows below the HTML content."
}

OR

* HTML CHOOSE BY VARIABLE (like video_id in StepVideo.vue)
{
  "html": {
    "op": "value",
    "variable": "my_variable",
    "values": {
      "<p>html snippet for <strong>value_1</strong></p>": "value_1",
      "<p>html snippet for <strong>value_2</strong></p>": "value_2",
      "<p>html snippet for <strong>value_3</strong></p>": "value_3"
    },
    "default": "<p>Default HTML if no value matches</p>"
  }
}

OR

* HTML CHOOSE BY VARIABLE WITH RANGE (like StepVideo.vue range mode)
{
  "html": {
    "op": "range",
    "variable": "score_variable",
    "values": {
      "<p>Low score content</p>": { "min": 0, "max": 50 },
      "<p>Medium score content</p>": { "min": 51, "max": 75 },
      "<p>High score content</p>": { "min": 76, "max": 100 }
    },
    "default": "<p>Default HTML if no range matches</p>"
  }
}

OR

* HTML CHOOSE BY DATASET VARIABLE (like StepPrePost.vue questionSets)
* Supports: "caseData" (default), "avatar", "user"
* Also supports nested properties like "avatar.id" in variable
{
  "html": {
    "op": "value",
    "dataset": "avatar",
    "variable": "id",
    "values": {
      "<p>Content for avatar 1</p>": "1",
      "<p>Content for avatar 2</p>": "2",
      "<p>Content for avatar 3</p>": "3"
    },
    "default": "<p>Default HTML if no value matches</p>"
  }
}

OR

* ACTION BUTTONS (like StepVideo.vue)
* Supported actions: "changeStep", "changeSubStep", "show-credit", "show-library", "link"
* For "link" action: use "newWindow": true to open in new tab
{
  "html": "<p>Your content here</p>",
  "messageBox": "Congratulations! Click the button below to continue.",
  "messageBoxBgColor": "blue",
  "messageBoxTxtColor": "white",
  "actionButtons": [
    {
      "label": "Begin next case",
      "color": "warning",
      "action": "changeStep",
      "actionValue": "2",
      "class": "caption font-weight-bold"
    },
    {
      "label": "Visit External Site",
      "color": "primary",
      "action": "link",
      "actionValue": "https://example.com",
      "newWindow": true
    }
  ]
}
 */
import { mapState, mapMutations, mapActions } from "vuex";
import StepTemplate from "/src/components/steps/StepTemplate.vue";

export default {
  name: "StepHtml",
  components: {
    StepTemplate,
  },
  props: {
    steps: {
      type: Array,
      required: true,
    },
    step: {
      type: Object,
      required: true,
      default: () => {
        return {};
      },
    },
    subStep: {
      type: Object,
      required: true,
      default: () => {
        return {};
      },
    },
    html: {
      type: [String, Object],
      required: true,
      default: "",
    },
    title: {
      type: String,
      required: false,
      default: "",
    },
    messageAbove: {
      type: String,
      required: false,
      default: "",
    },
    messageBox: {
      type: String,
      required: false,
      default: "",
    },
    messageBoxBgColor: {
      type: String,
      required: false,
      default: "#f0faff",
    },
    messageBoxTxtColor: {
      type: String,
      required: false,
      default: "#304156",
    },
    messageBelow: {
      type: String,
      required: false,
      default: "",
    },
    bannerSrc: {
      type: String,
      required: false,
      default: "",
    },
    actionButtons: {
      type: Array,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      htmlContent: "",
    };
  },
  computed: {
    ...mapState({
      caseData: (state) => state.caseData,
      avatar: (state) => state.avatar,
      user: (state) => state.user,
    }),
  },
  created() {
    this.init();
  },
  methods: {
    ...mapMutations(["setCurrentStep", "setCurrentSubStep", "setShowCredit", "setShowLibrary", "setContinueButtonEnabled"]),
    ...mapActions(["setCaseData"]),
    init() {
      this.setHtmlContentFromProp();
      this.setContinueButtonEnabled(true);
    },
    setHtmlContentFromProp() {
      if (typeof this.html === "string") {
        // Simple string HTML
        this.htmlContent = this.html;
      } else {
        // Object with conditional logic
        const htmlOptions = this.html;
        if (htmlOptions.op === "range") {
          this.htmlContent = this.getHtmlByRange(htmlOptions);
        } else if (htmlOptions.op === "value") {
          this.htmlContent = this.getHtmlByValue(htmlOptions);
        }
      }
    },
    getDataSet(config) {
      if (config.dataset === undefined || config.dataset === null || config.dataset === "caseData") {
        return this.caseData;
      } else if (config.dataset === "user") {
        return this.user;
      } else if (config.dataset === "avatar") {
        return this.avatar;
      }
      return this.caseData;
    },
    getVariableValue(dataSet, variableName) {
      if (!dataSet || !variableName) return undefined;
      
      // Support nested properties like "avatar.id" or "user.email"
      if (variableName.includes('.')) {
        const parts = variableName.split('.');
        let value = dataSet;
        for (const part of parts) {
          if (value === undefined || value === null) return undefined;
          value = value[part];
        }
        return value;
      }
      
      return dataSet[variableName];
    },
    getHtmlByRange(htmlOptions) {
      const dataSet = this.getDataSet(htmlOptions);
      const valueToCompare = this.getVariableValue(dataSet, htmlOptions.variable);
      for (const htmlSnippet in htmlOptions.values) {
        const range = htmlOptions.values[htmlSnippet];
        if (range.min <= valueToCompare && valueToCompare <= range.max) {
          return htmlSnippet;
        }
      }
      // Return default if no range matches
      return htmlOptions.default || "";
    },
    getHtmlByValue(htmlOptions) {
      const dataSet = this.getDataSet(htmlOptions);
      const valueToCompare = this.getVariableValue(dataSet, htmlOptions.variable);
      for (const htmlSnippet in htmlOptions.values) {
        const value = htmlOptions.values[htmlSnippet];
        if (value == valueToCompare) {
          return htmlSnippet;
        }
      }
      // Return default if no value matches
      return htmlOptions.default || "";
    },
    customButtonClick(button) {
      if (!button.action) {
        return;
      }
      if (button.action === "changeSubStep") {
        this.setCurrentSubStep(parseInt(button.actionValue));
      } else if (button.action === "changeStep") {
        this.clearCurrentCaseData();
        this.setCurrentStep(parseInt(button.actionValue));
      } else if (button.action === "show-credit") {
        this.setShowCredit(true);
      } else if (button.action === "show-library") {
        this.setShowLibrary(true);
      } else if (button.action === "link") {
        // Open external link, optionally in new window
        if (button.newWindow) {
          window.open(button.actionValue, '_blank');
        } else {
          window.location.href = button.actionValue;
        }
      }
    },
    clearCurrentCaseData() {
      this.setCaseData({});
    },
  },
};
</script>

<style scoped>
.html-content {
  font-size: 1.1em;
  line-height: 1.6em;
}
</style>
